// Building Intelligence — Phase 1 catalog.
// Industry + property setting drive the SUGGESTED starting areas & features.
// Banking is fully built out; office/medical/retail/education/other get a common
// building set plus relevant starter areas. Banking-specific areas never appear
// for non-banking industries by default. Custom areas/features are allowed anywhere.

export const PRESENCE = [
  { key: 'present', label: 'Present' },
  { key: 'not_present', label: 'Not present' },
  { key: 'unknown', label: 'Unknown / not verified' },
  { key: 'na', label: 'Not applicable' },
  { key: 'inaccessible', label: 'Inaccessible' },
];

export const RESP_PARTIES = [
  { key: 'client', label: 'Client' },
  { key: 'landlord', label: 'Landlord / Property Mgmt' },
  { key: 'association', label: 'Association' },
  { key: 'tfs', label: 'TFS' },
  { key: 'other_vendor', label: 'Other vendor' },
  { key: 'shared', label: 'Shared responsibility' },
  { key: 'unknown', label: 'Unknown' },
];

export const SITE_PLACEMENTS = [
  { key: 'corner', label: 'Corner lot / end unit' },
  { key: 'between', label: 'Between buildings' },
  { key: 'standalone_mid', label: 'Standalone (mid-block)' },
  { key: 'other', label: 'Other' },
];

// Common interior areas for every industry.
const COMMON = [
  { type: 'lobby', name: 'Lobby / Main Entrance' },
  { type: 'offices', name: 'Offices' },
  { type: 'conference', name: 'Conference Room' },
  { type: 'break_room', name: 'Break Room' },
  { type: 'restrooms', name: 'Restrooms' },
  { type: 'hallways', name: 'Hallways / Corridors' },
  { type: 'back_of_house', name: 'Back-of-House / Storage / Utility' },
  { type: 'stairwell', name: 'Stairwell' },
  { type: 'elevator', name: 'Elevator' },
];

// Industry-specific starter areas (added to COMMON). Banking is fully built out.
const BY_INDUSTRY = {
  banking: {
    fullyDeveloped: true,
    areas: [
      { type: 'teller', name: 'Teller Line' },
      { type: 'atm_interior', name: 'ATM — Interior' },
      { type: 'atm_drivethru', name: 'ATM — Drive-Through' },
      { type: 'night_deposit', name: 'Night Deposit' },
      { type: 'drive_through', name: 'Drive-Through Lanes' },
      { type: 'vault', name: 'Vault / Safe Deposit' },
      { type: 'loan_offices', name: 'Loan / Platform Offices' },
    ],
  },
  medical: {
    fullyDeveloped: false,
    areas: [
      { type: 'reception', name: 'Reception / Check-in' },
      { type: 'waiting', name: 'Waiting Room' },
      { type: 'exam', name: 'Exam Rooms' },
      { type: 'nurse_station', name: 'Nurse Station' },
      { type: 'lab', name: 'Lab' },
    ],
  },
  retail: {
    fullyDeveloped: false,
    areas: [
      { type: 'sales_floor', name: 'Sales Floor' },
      { type: 'fitting', name: 'Fitting Rooms' },
      { type: 'stock', name: 'Stock Room / Storage' },
      { type: 'checkout', name: 'Checkout / POS' },
      { type: 'loading', name: 'Loading Area' },
    ],
  },
  education: {
    fullyDeveloped: false,
    areas: [
      { type: 'classroom', name: 'Classrooms' },
      { type: 'cafeteria', name: 'Cafeteria' },
      { type: 'gym', name: 'Gymnasium' },
      { type: 'library', name: 'Library' },
      { type: 'admin_office', name: 'Administrative Office' },
    ],
  },
  commercial_office: { fullyDeveloped: false, areas: [] },
  government: { fullyDeveloped: false, areas: [] },
  industrial: {
    fullyDeveloped: false,
    areas: [
      { type: 'warehouse_floor', name: 'Warehouse Floor' },
      { type: 'dock', name: 'Loading Dock' },
      { type: 'shipping', name: 'Shipping / Receiving' },
    ],
  },
  other: { fullyDeveloped: false, areas: [] },
};

// Exterior / feature prompts — refined by property setting. "Ask whether it exists"
// (never assume). Tenant/retail-unit settings de-emphasize site-wide exterior items.
const EXTERIOR_FEATURES = [
  { key: 'dedicated_parking', name: 'Dedicated parking lot', settings: ['standalone'] },
  { key: 'shared_parking', name: 'Shared parking', settings: ['tenant_commercial', 'retail_center'] },
  { key: 'landscape', name: 'Landscaping', settings: ['standalone'] },
  { key: 'monument_sign', name: 'Monument / freestanding sign', settings: ['standalone', 'retail_center'] },
  { key: 'building_sign', name: 'Building-mounted sign', settings: ['standalone', 'tenant_commercial', 'retail_center', 'other'] },
  { key: 'exterior_lighting', name: 'Exterior lighting', settings: ['standalone', 'retail_center'] },
  { key: 'dumpster', name: 'On-site dumpster / enclosure', settings: ['standalone', 'retail_center'] },
];

const BANKING_FEATURES = [
  { key: 'atm', name: 'ATM(s)' },
  { key: 'drive_through', name: 'Drive-through' },
  { key: 'night_deposit', name: 'Night deposit' },
  { key: 'canopy', name: 'Canopy / awning / removable covering' },
];

export function suggestedAreas(industry) {
  const extra = (BY_INDUSTRY[industry] || BY_INDUSTRY.other).areas || [];
  return [...extra, ...COMMON];
}

export function featurePrompts(industry, setting) {
  const ext = EXTERIOR_FEATURES.filter((f) => !f.settings || f.settings.includes(setting));
  const bank = industry === 'banking' ? BANKING_FEATURES : [];
  return [...bank, ...ext];
}

export function industryIsFullyDeveloped(industry) {
  return !!(BY_INDUSTRY[industry] || {}).fullyDeveloped;
}

export const INDUSTRY_LABELS = {
  banking: 'Banking', medical: 'Medical / Healthcare', commercial_office: 'Commercial Office',
  education: 'Education', retail: 'Retail', government: 'Government',
  industrial: 'Industrial / Warehouse', other: 'Other',
};
export const SETTING_LABELS = {
  standalone: 'Standalone building', tenant_commercial: 'Tenant within a commercial building',
  retail_center: 'Retail / shopping-center unit', other: 'Other',
};
